import React from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useLinkLineAccount } from "../../features/invitation/hooks"
import { Button } from "src/components/ui/button"
import { CheckIcon, CircleAlertIcon, LoaderIcon, UserRound } from "lucide-react"
import { useAuth } from "src/providers/LoginProvider"
import { ApiError } from "src/openapi"

export default function LinkLineAccount() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const { lineIdToken } = useAuth()

  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [submissionCompleted, setSubmissionCompleted] = React.useState(false)
  const [linkError, setLinkError] = React.useState<string | null>(null)

  const code = searchParams.get("code") || ""
  if (!code) {
    navigate("/")
  }

  const { companyName, userName, linkLineAccount } = useLinkLineAccount(code)

  const submit = async () => {
    if (!lineIdToken) {
      return
    }
    setIsSubmitting(true)
    try {
      await linkLineAccount({
        idToken: lineIdToken,
        linkCode: code,
      })
      setSubmissionCompleted(true)
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 400) {
          setLinkError(e.body)
        } else {
          setLinkError("時間をおいて再度お試しください。")
        }
      } else {
        setLinkError("時間をおいて再度お試しください。")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (window.location.hostname !== "localhost" && !lineIdToken) {
    return (
      <div className="w-full h-full flex flex-col gap-4">
        <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
          <img src="/triple_logo.png" alt="logo" className="h-4" />
        </div>
        <div className="w-full flex flex-col justify-center items-center gap-4 p-4">
          <p className="text-md">LINEミニアプリからアクセスしてください。</p>
        </div>
      </div>
    )
  }

  if (linkError) {
    return (
      <div className="w-full h-full flex flex-col gap-4">
        <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
          <img src="/triple_logo.png" alt="logo" className="h-4" />
        </div>
        <div className="w-full flex flex-col justify-center items-center gap-4 p-4">
          <p className="text-lg font-bold">{companyName}</p>
          <CircleAlertIcon className="w-16 h-16 text-manual-red bg-white rounded-full" />
          <p className="text-md">処理中にエラーが発生しました。</p>
          <p className="text-md">{linkError}</p>
        </div>
      </div>
    )
  }

  if (submissionCompleted) {
    return (
      <div className="w-full h-full flex flex-col gap-4">
        <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
          <img src="/triple_logo.png" alt="logo" className="h-4" />
        </div>
        <div className="w-full flex flex-col justify-center items-center gap-4 p-4">
          <p className="text-lg font-bold">{companyName}</p>
          <CheckIcon className="w-16 h-16 text-accent bg-primary rounded-full" />
          <p className="text-md">アカウントの紐付けが完了しました。</p>
          <p className="text-md">このままアプリを閉じてください。</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col gap-4">
      <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
        <img src="/triple_logo.png" alt="logo" className="h-4" />
      </div>
      <div className="w-full flex flex-col justify-center p-1">
        <div className="w-full flex flex-col justify-center items-center gap-1">
          <p className="text-lg font-bold">{companyName}</p>
          <p className="text-lg font-bold">LINEアカウント紐付け</p>
        </div>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col items-center gap-4">
            <p className="text-md">
              このLINEアカウントを以下のユーザーに紐付けます。よろしいですか？
            </p>
            <div className="rounded-full bg-primary/10 p-2">
              <UserRound className="h-20 w-20 fill-primary text-primary" />
            </div>
            <p className="text-md">{userName}</p>
          </div>
          <div className="flex flex-col gap-1 sticky bottom-0 bg-white p-2">
            <Button
              variant="outline"
              className="rounded-full bg-gradient-to-r from-manual-from to-manual-to"
              disabled={isSubmitting}
              onClick={() => submit()}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-1">
                  <LoaderIcon className="h-5 w-5 animate-spin" />
                  <span>処理中</span>
                </div>
              ) : (
                <span className="font-bold">実行</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
